import { useMemo, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useUI from 'hooks/use-ui';
import useDispensaryDeliveryInfo from 'dispensary/hooks/use-dispensary-delivery-info';
import { useUserSegments } from 'hooks/use-user-segments';

import { consumerDispensaries } from 'shared/graphql/dispensary/queries';
import { useQuery } from 'hooks/use-query';

export const getQuery = () => consumerDispensaries;

export const getVariables = (dispensaryId, filters, userSegmentIds) => ({
  dispensaryFilter: {
    cNameOrID: dispensaryId,
    ...filters,
  },
  userSegmentIds,
});

export const getDispensary = async (apolloClient, props) =>
  apolloClient.query({
    query: getQuery(),
    variables: getVariables(props.params.cName),
    skip: !props.params.cName,
  });

function getError(query) {
  // eslint-disable-next-line lodash/prefer-lodash-method
  const params = Object.keys(query).join(', ');
  const error = new Error(
    `useDispensary may only be called from a route with a cName parameter. Found keys: ${params}`
  );
  error.query = query;

  return error;
}

export default function useDispensary(id) {
  const { isFirstRender } = useUI();
  const { isReady, query } = useRouter();
  const flags = useFlags();

  const { cName, dispensaryId: dispoIdFromQuery, cname } = query;
  /**
   * Context for different cname identifier params:
   * - cName: The canonical dispensary name used in the main URL structure
   * - cname: Alternative lowercase variant used specifically in pay/signup routes query params
   * We need to support both casing variants (cName and cname) to ensure all route patterns
   * can correctly identify the dispensary regardless of which URL pattern was used to access it.
   */
  const dispensaryId = id || cName || dispoIdFromQuery || cname;

  const { data, loading, error, ...apolloValues } = useQuery(consumerDispensaries, {
    variables: getVariables(dispensaryId, undefined, undefined),
    skip: isFirstRender || !isReady || !dispensaryId,
  });

  const dispensary =
    _.find(data?.filteredDispensaries, (d) => d.cName === cName || d.id === dispensaryId) ||
    _.head(data?.filteredDispensaries);

  // We must fetch the dispensary first to get its id before we can fetch user segments.
  // This requires an initial query followed by a refetch with segments once available.
  const { segments: userSegments } = useUserSegments({ dispensaryId: dispensary?.id });
  const isExclusiveCollectionsEnabled = flags['ecommerce.exclusive-collections.rollout'] ?? false;

  // Refetch with segments once we have the dispensary ObjectId and user segments
  // Use ref to prevent unnecessary refetches if segments have already been applied
  const segmentsAppliedRef = useRef(false);
  const lastDispensaryIdRef = useRef(null);
  const { refetch } = apolloValues;

  // Create stable, sorted segments for consistent cache keys
  // Sort segments to ensure consistent cache keys regardless of backend order
  const sortedSegments = useMemo(() => [...userSegments].sort(), [userSegments]);
  const segmentKey = useMemo(() => sortedSegments.join(','), [sortedSegments]);

  useEffect(() => {
    const shouldIncludeSegments = isExclusiveCollectionsEnabled && dispensary?.id && sortedSegments.length > 0;

    // Reset ref when navigating to a different dispensary
    if (lastDispensaryIdRef.current !== dispensary?.id) {
      segmentsAppliedRef.current = false;
      lastDispensaryIdRef.current = dispensary?.id;
    }

    if (shouldIncludeSegments && !loading && !segmentsAppliedRef.current) {
      segmentsAppliedRef.current = true;
      const variablesWithSegments = getVariables(dispensaryId, undefined, sortedSegments);
      refetch(variablesWithSegments);
    }
    // Reset when segments are no longer needed (e.g., feature flag disabled or user logged out)
    if (!shouldIncludeSegments) {
      segmentsAppliedRef.current = false;
    }
    // Using segmentKey (joined segments) instead of userSegments array to prevent triggering
    // on reference changes when contents are identical, while still detecting content changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExclusiveCollectionsEnabled, dispensary?.id, segmentKey, loading, refetch, dispensaryId]);

  const deliveryInfoResult = useDispensaryDeliveryInfo(isReady ? dispensaryId : null);

  const finalDispensary = useMemo(() => {
    if (!deliveryInfoResult || !dispensary) {
      return dispensary;
    }
    return {
      ...dispensary,
      deliveryInfo: deliveryInfoResult.deliveryInfo || dispensary.deliveryInfo,
      taxConfig: {
        ...dispensary.taxConfig,
        taxes: deliveryInfoResult.taxes || dispensary.taxConfig.taxes,
      },
    };
  }, [dispensary, deliveryInfoResult]);

  if (isReady && !dispensaryId) {
    return {
      data,
      loading,
      error: getError(query),
      dispensary: finalDispensary,
      ...apolloValues,
    };
  }

  return {
    data,
    loading: isFirstRender || !isReady || loading,
    error,
    dispensary: finalDispensary,
    dispensaryId,
    ...apolloValues,
  };
}
